import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSignal } from "@preact/signals-react";
import { useIntl } from "react-intl";
import { Link } from "react-router";
import { cn } from "tools/ui";

const baseIcon = "h-6 w-6 fill-current";
const HOVER_LEAVE_DELAY = 200;
const PANEL_OFFSET = 0;
const CLAMP_MARGIN = 8;

function HoverPanel({ pos, onMouseEnter, onMouseLeave, className, children }) {
  const ref = useRef(null);
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!pos || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const margin = CLAMP_MARGIN;
    let left = pos.x + PANEL_OFFSET;
    if (left + rect.width > window.innerWidth - margin) {
      left = pos.x - rect.width - PANEL_OFFSET;
    }
    const top = Math.min(pos.y, window.innerHeight - rect.height - margin);
    setCoords({
      left: Math.max(margin, left),
      top: Math.max(margin, top),
    });
  }, [pos]);

  if (!pos) return null;

  return createPortal(
    <div
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn("fixed z-50", className)}
      style={coords || { left: pos.x + PANEL_OFFSET, top: pos.y }}
    >
      {children}
    </div>,
    document.body,
  );
}

function MenuItem({ item, currentMenu, openSidebar, isDesktop }) {
  const intl = useIntl();
  const itemName = intl.formatMessage({ id: `menu.${item.name}` });
  const hover = useSignal(null);
  const leaveTimer = useRef(null);

  function clearLeaveTimer() {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  }

  function handleMouseEnter(e) {
    clearLeaveTimer();
    if (!openSidebar.value) return;
    const rect = e.currentTarget.getBoundingClientRect();
    hover.value = { x: rect.right, y: rect.top };
  }

  function handleMouseLeave() {
    clearLeaveTimer();
    leaveTimer.current = setTimeout(() => {
      hover.value = null;
    }, HOVER_LEAVE_DELAY);
  }

  function handleClick() {
    currentMenu.value = item.name;
    hover.value = null;
    if (!isDesktop) {
      openSidebar.value = false;
    }
  }

  function childrenLinks() {
    return item.children.map((child, idx) => {
      const [key, value] = Object.entries(child)[0];
      const childName = intl.formatMessage({ id: `menu.${value}` });

      if (child?.visible) return false;

      return (
        <Link
          key={`${key}-${idx}`}
          to={`${item.path}/${value}`}
          className="block text-gray-400 hover:text-yellow-300 text-[10px] uppercase transition-colors duration-200"
          onClick={handleClick}
        >
          {value.Icon && (
            <value.Icon className="inline w-4 h-4 mr-1 align-text-bottom" />
          )}
          {childName}
        </Link>
      );
    });
  }

  const styleCurrentIcon = "";

  const collapsed = openSidebar.value && isDesktop;

  const styleOpenMenu = collapsed
    ? "h-12 w-14 mb-1"
    : "w-full md:w-full md:h-fit";

  const isCurrent = currentMenu.value === item.name;
  return (
    <div
      className={styleOpenMenu}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Link
        replace
        key={item.name}
        to={item.path}
        onClick={handleClick}
        className={cn(
          "flex items-center cursor-pointer w-full px-1 py-3 text-xs md:text-sm text-center active:scale-95",
          collapsed
            ? "hover:bg-neutral-900 hover:rounded-s-md hover:text-amber-300"
            : "hover:text-amber-300",
          isCurrent
            ? collapsed
              ? "text-amber-400 rounded-md bg-neutral-900"
              : "text-amber-400"
            : "text-gray-100",
          collapsed ? "" : "space-x-3",
        )}
      >
        {item.icon && (
          <item.icon
            className={cn(
              baseIcon,
              styleCurrentIcon,
              collapsed ? "mx-auto" : "",
            )}
            aria-hidden="true"
          />
        )}
        {!collapsed && (
          <span className="uppercase break-words text-xs">{itemName}</span>
        )}
      </Link>

      {item.children && (
        <div
          className={cn(
            "h-fit animate-fade-in border-l border-neutral-600 space-y-2.5 transition-all duration-300",
            !collapsed ? "block mt-1 pl-3 ml-4" : "hidden",
          )}
        >
          {childrenLinks()}
        </div>
      )}

      {collapsed && hover.value && (
        <HoverPanel
          pos={hover.value}
          onMouseEnter={clearLeaveTimer}
          onMouseLeave={handleMouseLeave}
          className={cn(
            "bg-neutral-900",
            item.children
              ? "w-52  space-y-2.5 bg-neutral-600 rounded-md rounded-tl-none p-3"
              : "w-max h-12  flex items-center max-w-52 uppercase rounded-r-md py-2 px-3 text-xs text-white",
          )}
        >
          {item.children ? (
            <>
              <Link replace to={item.path} onClick={handleClick}>
                <div className="text-white uppercase border-b border-neutral-600 mb-3 pb-1 cursor-pointer hover:text-amber-300 transition-colors duration-200 max-w-max text-xs">
                  {itemName}
                </div>
              </Link>
              {childrenLinks()}
            </>
          ) : (
            itemName
          )}
        </HoverPanel>
      )}
    </div>
  );
}

export default MenuItem;
